export enum OrderStatus {
	UNSUBMITTED = '1',
	PENDING = '2',
	SENT_TO_ERP = '3',
	APPROVED = '4',
	PARTIALLY_SHIPPED = 'Partially Shipped',
	SHIPPED = '5',
	HELD = '6',
	CANCELLED = '7',
	APPROVAL_REQUIRED = '8',
	OVERRIDE_REJECTED = '9',
	CANCELLATION_REQUESTED = '10',
	OVERRIDE_APPROVED = '11',
	SUBMITTED = '12',
	QUOTE = '13',
	QUOTE_NEEDS_REVIEW = 'Quote - Needs Review',
	// Pre-approval hold for the order-day license check (PD-14806/PD-14807). An
	// order enters this status at placement when the tenant's
	// licensing.order_license_check setting is ON, and leaves it only when every
	// stamped license is verified good AS OF TODAY (-> the tenant's real
	// submission status) or a check fails/times out (-> APPROVAL_REQUIRED +
	// compliance email). Deliberately NOT in DEFAULT_SUBMISSION_STATUS_OPTIONS:
	// the external mover-integration exports on APPROVED, so an order parked here
	// is invisible to the ERP until the check passes.
	PENDING_LICENSE_CHECK = 'Pending License Check',
}

export type OrderStatusKey = keyof typeof OrderStatus;
export type OrderStatusValue = (typeof OrderStatus)[OrderStatusKey];

/**
 * Human-readable labels for order statuses
 */
export const OrderStatusLabels: Record<string, string> = {
	[OrderStatus.UNSUBMITTED]: 'Unsubmitted',
	[OrderStatus.PENDING]: 'Pending',
	[OrderStatus.SENT_TO_ERP]: 'Sent to ERP',
	[OrderStatus.APPROVED]: 'Approved',
	[OrderStatus.SHIPPED]: 'Shipped',
	[OrderStatus.HELD]: 'Held',
	[OrderStatus.CANCELLED]: 'Cancelled',
	[OrderStatus.APPROVAL_REQUIRED]: 'Approval Required',
	[OrderStatus.OVERRIDE_REJECTED]: 'Override Rejected',
	[OrderStatus.CANCELLATION_REQUESTED]: 'Cancellation Requested',
	[OrderStatus.OVERRIDE_APPROVED]: 'Override Approved',
	[OrderStatus.SUBMITTED]: 'Submitted',
	[OrderStatus.QUOTE]: 'Quote',
	[OrderStatus.PARTIALLY_SHIPPED]: 'Partially Shipped',
	[OrderStatus.QUOTE_NEEDS_REVIEW]: 'Quote - Needs Review',
	[OrderStatus.PENDING_LICENSE_CHECK]: 'Pending License Check',
};

/**
 * Statuses for Abandoned Carts report (orders in cart, not submitted)
 */
export const AbandonedCartStatuses: Array<string> = [OrderStatus.UNSUBMITTED];

/**
 * Statuses for Quotes report
 */
export const QuoteStatuses: Array<string> = [
	OrderStatus.QUOTE,
	OrderStatus.QUOTE_NEEDS_REVIEW,
];

/**
 * Get human-readable status label from status code
 */
export function getOrderStatusLabel(
	status: string | number | null | undefined,
): string {
	if (status == null) {
		return '-';
	}

	const statusCode = String(status);

	return OrderStatusLabels[statusCode] ?? 'Unknown';
}

/**
 * Get status color based on status code
 */
export function getOrderStatusColor(
	status: string | number | null | undefined,
): string {
	if (status == null) {
		return 'default';
	}

	const statusCode = String(status);

	switch (statusCode) {
		case OrderStatus.UNSUBMITTED:
			return 'secondary';
		case OrderStatus.PENDING:
			return 'warning';
		case OrderStatus.SENT_TO_ERP:
			return 'info';
		case OrderStatus.APPROVED:
			return 'success';
		case OrderStatus.SHIPPED:
			return 'success';
		case OrderStatus.HELD:
			return 'warning';
		case OrderStatus.CANCELLED:
			return 'danger';
		case OrderStatus.APPROVAL_REQUIRED:
			return 'warning';
		case OrderStatus.OVERRIDE_REJECTED:
			return 'danger';
		case OrderStatus.CANCELLATION_REQUESTED:
			return 'danger';
		case OrderStatus.OVERRIDE_APPROVED:
			return 'success';
		case OrderStatus.SUBMITTED:
			return 'info';
		case OrderStatus.QUOTE:
			return 'secondary';
		case OrderStatus.PARTIALLY_SHIPPED:
			return 'info';
		case OrderStatus.QUOTE_NEEDS_REVIEW:
			return 'warning';
		case OrderStatus.PENDING_LICENSE_CHECK:
			return 'warning';
		default:
			return 'default';
	}
}

/**
 * Order origin enum - indicates where the order was created
 */
export enum OrderOrigin {
	CRM = '1',
	ERP = '2',
	ECOMM = '3',
}

export type OrderOriginKey = keyof typeof OrderOrigin;
export type OrderOriginValue = (typeof OrderOrigin)[OrderOriginKey];

/**
 * Human-readable labels for order origins
 */
export const OrderOriginLabels: Record<string, string> = {
	[OrderOrigin.CRM]: 'CRM',
	[OrderOrigin.ERP]: 'ERP',
	[OrderOrigin.ECOMM]: 'Ecomm',
};

/**
 * Get human-readable origin label from origin code
 */
export function getOrderOriginLabel(
	origin: string | number | null | undefined,
): string {
	if (origin == null) {
		return '-';
	}

	const originCode = String(origin);

	return OrderOriginLabels[originCode] ?? '-';
}
